import { API_BASE_URL, TEMPLATES_BASE_URL, staticFetch, apiFetch } from "./api.js";

const isAbsoluteUrl = (value) => /^https?:\/\//i.test(String(value || "").trim());

const normalizeTemplateCollection = (catalog) => (Array.isArray(catalog) ? catalog : []);

const getTemplateIdentity = (template) =>
    template?.manifest?.id ||
    template?.id ||
    template?.template_id ||
    template?.templateId ||
    template?.slug ||
    template?.vertical ||
    template?.name ||
    template?.title ||
    "";

const joinBaseUrl = (baseUrl, assetPath) => {
    const rawPath = String(assetPath || "").trim();
    if (!rawPath) return "";
    if (isAbsoluteUrl(rawPath)) return rawPath;
    const cleanBase = String(baseUrl || "").replace(/\/+$/, "");
    const cleanPath = rawPath.startsWith("/") ? rawPath : `/${rawPath}`;
    return `${cleanBase}${cleanPath}`;
};

const withNoCacheQuery = (url) => {
    const rawUrl = String(url || "").trim();
    if (!rawUrl) return rawUrl;

    try {
        const nextUrl = new URL(rawUrl, window.location.origin);
        nextUrl.searchParams.set("_hwts", String(Date.now()));
        return nextUrl.toString();
    } catch {
        return rawUrl;
    }
};

const withQueryFlag = (url, key, value) => {
    if (!url) return "";
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
};

const cloneValue = (value) => JSON.parse(JSON.stringify(value || {}));
const OPTIONAL_SECTION_KEY_PREFIXES = ["sectionVisibility:", "sectionOrder:"];

const extractOptionalSectionState = (value) => {
    const source = value && typeof value === "object" ? value : {};
    const next = {};

    for (const [field, entry] of Object.entries(source)) {
        const key = String(field || "").trim();
        if (!OPTIONAL_SECTION_KEY_PREFIXES.some((prefix) => key.startsWith(prefix))) continue;
        next[key] = cloneValue(entry);
    }

    return next;
};

const withOptionalSectionState = (url, value) => {
    const rawUrl = String(url || "").trim();
    if (!rawUrl) return rawUrl;

    const optionalSections = extractOptionalSectionState(value);
    if (!Object.keys(optionalSections).length) return rawUrl;

    try {
        const nextUrl = new URL(rawUrl, window.location.origin);
        nextUrl.searchParams.set("heyweb_sections", JSON.stringify(optionalSections));
        return nextUrl.toString();
    } catch {
        return rawUrl;
    }
};

const isSharedStateField = (field) => {
    const key = String(field || "").trim().toLowerCase();
    if (!key) return false;
    if (key.startsWith("header:global:")) return true;
    if (key.startsWith("banner:")) return true;
    if (key.startsWith("logo:")) return true;
    if (key.startsWith("i18n:header.")) return true;
    if (key.startsWith("i18n:search.")) return true;
    if (key.startsWith("i18n:locale.")) return true;
    if (key.includes(":headerglobal:")) return true;
    return false;
};

const extractSharedState = (value) => {
    const source = value && typeof value === "object" ? value : {};
    const next = {};

    for (const [field, entry] of Object.entries(source)) {
        if (!isSharedStateField(field)) continue;
        next[field] = cloneValue(entry);
    }

    return next;
};

const mergeEditorState = (pageContent, editorBackup) => {
    const safePageContent = pageContent && typeof pageContent === "object" ? cloneValue(pageContent) : {};
    const safeBackup = extractSharedState(editorBackup);
    const next = { ...safePageContent };

    Object.entries(safeBackup).forEach(([field, entry]) => {
        if (
            entry && typeof entry === "object" && !Array.isArray(entry) &&
            next[field] && typeof next[field] === "object" && !Array.isArray(next[field])
        ) {
            next[field] = {
                ...next[field],
                ...entry,
            };
            return;
        }

        next[field] = entry;
    });

    return next;
};

const fetchJson = async (url) => {
    const response = await fetch(withNoCacheQuery(url), {
        method: "GET",
        cache: "no-store",
        headers: {
            Accept: "application/json",
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
        },
    });
    const text = await response.text();
    const body = text ? JSON.parse(text) : null;
    if (!response.ok) {
        throw new Error(body?.message || body?.error || `HTTP ${response.status}`);
    }
    return body;
};

export const PublicSiteRuntime = {
    getPublicSitePath() {
        const pathname = String(window.location.pathname || "/").replace(/^\/+|\/+$/g, "").trim();
        const blockedPrefixes = ["assets", "css", "js", "dist"];
        if (
            pathname &&
            !pathname.startsWith("index.html") &&
            !blockedPrefixes.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))
        ) {
            return pathname;
        }

        const hash = String(window.location.hash || "").trim();
        const match = hash.match(/^#published\/(.+)$/);
        return match ? decodeURIComponent(match[1] || "").trim() : "";
    },

    isPublicPath() {
        return Boolean(this.getPublicSitePath());
    },

    async mount() {
        const sitePath = this.getPublicSitePath();

        if (!sitePath) return false;

        const app = document.getElementById("app");
        if (!app) return false;

        document.body.classList.add("public-site-runtime");
        const topbar = document.getElementById("topbar");
        const layout = document.getElementById("layout");
        const profilePanel = document.getElementById("profile-panel");
        const profileBackdrop = document.getElementById("profile-panel-backdrop");
        if (topbar) topbar.hidden = true;
        if (profilePanel) profilePanel.hidden = true;
        if (profileBackdrop) profileBackdrop.hidden = true;
        if (layout) layout.style.display = "block";

        app.innerHTML = `
            <section style="min-height:100vh; background:white;">
                <iframe id="public-site-frame" title="Site publicado" style="display:block; width:100%; min-height:100vh; border:0; background:white;"></iframe>
            </section>
        `;

        try {
            const [site, homePage, catalogResponse] = await Promise.all([
                fetchJson(`${API_BASE_URL}/api/public/sites/${encodeURIComponent(sitePath)}`),
                fetchJson(`${API_BASE_URL}/api/public/sites/${encodeURIComponent(sitePath)}/pages/home`),
                staticFetch(`/templates.json?v=${Date.now()}`),
            ]);

            const catalog = catalogResponse.ok ? normalizeTemplateCollection(await catalogResponse.json()) : [];
            const selectedTemplate = catalog.find((template) => {
                const templateId = getTemplateIdentity(template);
                return (
                    templateId === site?.template_id ||
                    template?.vertical === site?.template_id ||
                    template?.slug === site?.template_id
                );
            });

            if (!selectedTemplate) {
                throw new Error("Template publicado nao encontrado.");
            }

            document.title = site?.name ? `${site.name} | SiteEiro` : "SiteEiro";

            const previewPath =
                selectedTemplate?.preview_url ||
                selectedTemplate?.url ||
                site?.url ||
                `/${sitePath}/`;
            const iframe = document.getElementById("public-site-frame");
            const currentData = mergeEditorState(homePage?.content, site?.extra?.editor_content);
            const previewUrl = withOptionalSectionState(
                withQueryFlag(joinBaseUrl(TEMPLATES_BASE_URL, previewPath), "siteeiro_preview", "1"),
                currentData
            );

            const handleMessage = (event) => {
                if (event.origin !== new URL(TEMPLATES_BASE_URL).origin) return;
                if (!event.data || event.data.type !== "HEYWEB_READY") return;

                iframe?.contentWindow?.postMessage(
                    {
                        type: "HEYWEB_INIT",
                        data: currentData,
                    },
                    "*"
                );
            };

            window.addEventListener("message", handleMessage);
            iframe.src = previewUrl;
            return true;
        } catch (error) {
            app.innerHTML = `
                <section style="max-width:960px; margin:0 auto; padding:3rem 1.25rem;">
                    <div style="padding:2rem; background:#fff1f2; border:1px solid #fecdd3; color:#9f1239;">
                        <h1 style="margin:0 0 0.75rem; font-size:1.5rem;">Nao foi possivel abrir este site publicado</h1>
                        <p style="margin:0;">${error.message || "Erro inesperado."}</p>
                    </div>
                </section>
            `;
            return true;
        }
    },
};

export const DraftSiteRuntime = {
    getDraftSitePath() {
        const pathname = String(window.location.pathname || "/").trim();
        const match = pathname.match(/^\/preview\/([^/?#]+)\/?$/i);
        return match ? decodeURIComponent(match[1] || "").trim() : "";
    },

    isDraftPath() {
        return Boolean(this.getDraftSitePath());
    },

    async mount() {
        const sitePath = this.getDraftSitePath();
        if (!sitePath) return false;

        const app = document.getElementById("app");
        if (!app) return false;

        document.body.classList.add("public-site-runtime");
        const topbar = document.getElementById("topbar");
        const layout = document.getElementById("layout");
        const profilePanel = document.getElementById("profile-panel");
        const profileBackdrop = document.getElementById("profile-panel-backdrop");
        if (topbar) topbar.hidden = true;
        if (profilePanel) profilePanel.hidden = true;
        if (profileBackdrop) profileBackdrop.hidden = true;
        if (layout) layout.style.display = "block";

        app.innerHTML = `
            <section style="min-height:100vh; background:white;">
                <iframe id="draft-site-frame" title="Visualizacao do rascunho" style="display:block; width:100%; min-height:100vh; border:0; background:white;"></iframe>
            </section>
        `;

        try {
            const [siteResponse, homePageResponse, catalogResponse] = await Promise.all([
                apiFetch(`/my-sites/${encodeURIComponent(sitePath)}`, { method: "GET" }),
                apiFetch(`/my-sites/${encodeURIComponent(sitePath)}/pages/home`, { method: "GET" }),
                staticFetch(`/templates.json?v=${Date.now()}`),
            ]);

            const siteText = await siteResponse.text();
            const site = siteText ? JSON.parse(siteText) : null;
            if (!siteResponse.ok) {
                throw new Error(site?.message || site?.error || `HTTP ${siteResponse.status}`);
            }

            const homeText = await homePageResponse.text();
            const homePage = homeText ? JSON.parse(homeText) : null;
            if (!homePageResponse.ok) {
                throw new Error(homePage?.message || homePage?.error || `HTTP ${homePageResponse.status}`);
            }

            const catalog = catalogResponse.ok ? normalizeTemplateCollection(await catalogResponse.json()) : [];
            const selectedTemplate = catalog.find((template) => {
                const templateId = getTemplateIdentity(template);
                return (
                    templateId === site?.template_id ||
                    template?.vertical === site?.template_id ||
                    template?.slug === site?.template_id
                );
            });

            if (!selectedTemplate) {
                throw new Error("Template associado ao site nao encontrado.");
            }

            document.title = site?.name ? `${site.name} | SiteEiro` : "SiteEiro";

            const previewPath =
                selectedTemplate?.preview_url ||
                selectedTemplate?.url ||
                site?.url ||
                `/${sitePath}/`;
            const iframe = document.getElementById("draft-site-frame");
            const currentData = mergeEditorState(homePage?.content, site?.extra?.editor_content);
            const previewUrl = withOptionalSectionState(
                withQueryFlag(joinBaseUrl(TEMPLATES_BASE_URL, previewPath), "siteeiro_preview", "1"),
                currentData
            );

            const handleMessage = (event) => {
                if (event.origin !== new URL(TEMPLATES_BASE_URL).origin) return;
                if (!event.data || event.data.type !== "HEYWEB_READY") return;

                iframe?.contentWindow?.postMessage(
                    {
                        type: "HEYWEB_INIT",
                        data: currentData,
                    },
                    "*"
                );
            };

            window.addEventListener("message", handleMessage);
            iframe.src = previewUrl;
            return true;
        } catch (error) {
            app.innerHTML = `
                <section style="max-width:960px; margin:0 auto; padding:3rem 1.25rem;">
                    <div style="padding:2rem; background:#fff1f2; border:1px solid #fecdd3; color:#9f1239;">
                        <h1 style="margin:0 0 0.75rem; font-size:1.5rem;">Nao foi possivel abrir este rascunho</h1>
                        <p style="margin:0;">${error.message || "Erro inesperado."}</p>
                    </div>
                </section>
            `;
            return true;
        }
    },
};

